import React, { useState, useEffect, Fragment } from 'react';
import { Cookies } from 'react-cookie';
import {
  getUGCUserInfoByPathAuth,
  getUGCUserInfoById,
  getUGCUserInteractions,
} from '../../utilities/userUtil/userUtil';
import ProfileHeader from '../../components/ProfileHeader/ProfileHeader';
import ProfileNav from '../../components/ProfileNav/ProfileNav';
import InteractionList from '../../components/InteractionList/InteractionList';
import SettingsForm from '../../components/SettingsForm/SettingsForm';
import AvatarForm from '../../components/AvatarForm/AvatarForm';
import Analytics from '../../utilities/Analytics/Analytics';

/**
 * ProfilePage is the parent components for the profile page.
 * @returns {React.component} The profile page to render.
 */
const ProfilePage = (props) => {
  // Profile user data.
  const [user, setUser] = useState({
    id: parseInt(props.match.params.userId, 10) || null,
    name: null,
    imageUrl: 'https://u.o0bc.com/avatars/stock/_no-user-image.gif',
    aboutMe: null,
    profileVisibility: null,
  });

  // Profile user interactions.
  const [interactions, setInteractions] = useState([]);

  // Active user data.
  const [actingUser, setActingUser] = useState({
    id: null,
    canEdit: false,
    tier: 'USER',
  });

  // UI data
  const [activeView, setActiveView] = useState('activity');
  const [errorMessage, setErrorMessage] = useState(null);

  // Profile Visibility
  const profileVisibility = user.profileVisibility || 'PRIVATE';

  const isAdmin = actingUser.tier === 'ADMIN';

  // Fetch and set user data only when component is rendered.
  useEffect(() => {
    Analytics.pageView('profile');
    if (user.id) {
      fetchAndSetUserData();
    } else {
      setErrorMessage('We were unable to locate that user.');
    }
  }, []);

  /**
   * Fetches and sets profile and active user information.
   * @method setUserData.
   */
  const fetchAndSetUserData = async () => {
    const doFetchAndSetProfileUser = fetchAndSetProfileUser();
    const doFetchAndSetActingUser = fetchAndSetActingUser();

    return Promise.all([doFetchAndSetProfileUser, doFetchAndSetActingUser]);
  };

  /**
   * Fetches and sets profile user data from userId parameter.
   * @method setProfileUser
   */
  const fetchAndSetProfileUser = async () => {
    const ugcUserResponse = await getUGCUserInfoById(user.id);
    if (ugcUserResponse.users.length > 0) {
      const {
        id, aboutMe, imageUrl, name, profileVisibility,
      } = ugcUserResponse.users[0] || {};
      setUser({
        id, aboutMe, imageUrl, name, profileVisibility,
      });
    } else {
      setErrorMessage('We were unable to locate that user.');
    }
  };

  /**
   * Fetches and sets profile user interactions.
   * @method fetchAndSetProfileUserInteractions
   */
  const fetchAndSetInteractions = async () => {
    const ugcUserInteractions = await getUGCUserInteractions(user.id);
    const { interactions } = ugcUserInteractions || [];
    setInteractions(interactions);
  };

  /**
   * Fetches and sets active user data from pathAuth cookie.
   * @method setActiveUser
   */
  const fetchAndSetActingUser = async () => {
    const cookie = new Cookies();
    const pathAuth = cookie.get('pathAuth');

    // If pathAuth cookie exists, fetch user info.
    if (pathAuth) {
      const ugcUserResponse = await getUGCUserInfoByPathAuth(pathAuth);
      const actingUserId = ugcUserResponse.id || null;
      const canEdit = (actingUserId === user.id);
      const tier = ugcUserResponse.tier || 'USER';
      setActingUser({ actingUserId, canEdit, tier });
    }
  };

  /**
   * Update the active view based on ProfileNav interaction.
   */
  const updateActiveView = (event) => {
    setActiveView(event.currentTarget.name);
  };

  /**
   * Update the profile display based on active user interaction.
   */
  const updateProfileDisplay = (settings) => {
    setActiveView('activity');
    setUser(newUser => ({ ...newUser, ...settings }));
    document.body.scrollTop = document.documentElement.scrollTop = 0;
  };

  return (
    <Fragment>
      <ProfileHeader
        name={user.name}
        imageUrl={user.imageUrl}
        aboutMe={user.aboutMe}
        updateActiveView={updateActiveView}
        actingUserCanEdit={actingUser.canEdit}
        profileVisibility={profileVisibility}
        isAdmin={isAdmin}
      />
      <div className="contain">
        <ProfileNav
          activeView={activeView}
          actingUserCanEdit={actingUser.canEdit}
          updateActiveView={updateActiveView}
        />
        {((actingUser.canEdit || profileVisibility === 'PUBLIC' || isAdmin) && activeView === 'activity') &&
          <InteractionList fetchAndSetInteractions={fetchAndSetInteractions} interactions={interactions} />
        }
        {(actingUser.canEdit && activeView === 'settings') &&
          <SettingsForm
            userId={user.id}
            aboutMe={user.aboutMe}
            profileVisibility={profileVisibility}
            isAdmin={isAdmin}
            updateProfileDisplay={updateProfileDisplay}
          />
        }
        {(actingUser.canEdit && activeView === 'avatar') &&
          <AvatarForm
            userId={user.id}
            updateActiveView={updateActiveView}
            updateProfileDisplay={updateProfileDisplay}
          />
        }
        {(!actingUser.canEdit && errorMessage === null && profileVisibility === 'PRIVATE') &&
          <p>This profile is set to private.</p>
        }
        {(errorMessage !== null) &&
          <p>{errorMessage}</p>
        }
      </div>
    </Fragment>
  );
};

export default ProfilePage;



// WEBPACK FOOTER //
// ./src/components/ProfilePage/ProfilePage.js